// TODO: Ajustar regra de ordenação de imports (lint) conforme config do monorepo.
import { useMemo, useState } from 'react';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';
import cx from 'classnames';
import { accessibleOnClick } from 'govbr.frontend.policy/helpers';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PersonCard from '../../PersonCard/PersonCard';
import { folderPattern, personsGridClass } from '../ListingPersons/utils';

import AreaInfo from './components/AreaInfo';
import styles from './ListingPersons.module.less';

// Este componente agora recebe todos os itens da árvore via props (uma única consulta feita no componente raiz).
// Não executa mais consultas próprias.

const messages = defineMessages({
  viewArea: {
    id: 'listing.area.view',
    defaultMessage: 'View area',
  },
  editArea: {
    id: 'listing.area.edit',
    defaultMessage: 'Edit area',
  },
  addArea: {
    id: 'listing.area.add',
    defaultMessage: 'Add area',
  },
  addPerson: {
    id: 'listing.person.add',
    defaultMessage: 'Add person',
  },
  whoIsWho: {
    id: 'listing.persons.whoiswho',
    defaultMessage: 'Who is who',
  },
  areaToolsAria: {
    id: 'listing.area.tools',
    defaultMessage: 'Area tools',
  },
});

const areaDevToolsActions = (baseUrl, intl, messages) => [
  {
    id: 'view',
    href: `${baseUrl}`,
    icon: 'fa-eye',
    label: intl.formatMessage(messages.viewArea),
  },
  {
    id: 'edit',
    href: `${baseUrl}/edit`,
    icon: 'fa-edit',
    label: intl.formatMessage(messages.editArea),
  },
  {
    id: 'add-area',
    href: `${baseUrl}/add?type=Area`,
    icon: 'fa-plus-square',
    label: intl.formatMessage(messages.addArea),
  },
  {
    id: 'add-person',
    href: `${baseUrl}/add?type=Pessoa`,
    icon: 'fa-user-plus mr-1',
    label: intl.formatMessage(messages.addPerson),
  },
];

function AreaTreeNode({
  id,
  path,
  title,
  listingItems = [],
  isLoading,
  error,
  hasLoaded,
  personsPerRow = '3',
  personsShowDescription = false,
  showPersons = true,
  summaryCard = false,
  depth = 0,
  area,
  peopleLinks,
  showAreasDescriptions,
  showPersonsAllLinks,
  showPersonsSubstitute,
  isEditMode,
}) {
  const [expanded, setExpanded] = useState(false);
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);

  const getType = (item) => item?.portal_type || item?.['@type'];
  // Encontrar dados da área atual
  const { areaData, subAreas, persons } = useMemo(() => {
    const result = {
      areaData: area || { '@id': path, title },
      subAreas: [],
      persons: [],
    };

    // Primeiro localizar dados completos da área (caso não passados)
    if (!area) {
      const foundArea = listingItems.find(
        (it) => it['@id'] === path && getType(it) === 'Area',
      );
      if (foundArea) result.areaData = foundArea;
    }
    const baseUrl = result.areaData?.['@id'] || path;
    const basePrefix = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';

    for (const item of listingItems) {
      const id = item['@id'];
      if (!id || id === baseUrl || !id.startsWith(basePrefix)) continue;
      const rel = id.slice(basePrefix.length); // relative path inside area
      const segments = rel.split('/');
      const t = getType(item);
      if (segments.length === 1) {
        if (t === 'Area') result.subAreas.push(item);
        else if (t === 'Pessoa') result.persons.push(item);
      } else if (
        segments.length === 2 &&
        t === 'Pessoa' &&
        folderPattern.test(segments[0])
      ) {
        result.persons.push(item);
      }
    }

    return result;
  }, [area, listingItems, path, title]);

  const baseUrl = areaData?.['@id'] || path;
  const areaReviewStateClass =
    areaData?.review_state && areaData.review_state !== 'published'
      ? `review-state-${areaData.review_state}`
      : null;

  const devActions = useMemo(
    () => areaDevToolsActions(baseUrl, intl, messages),
    [baseUrl, intl],
  );

  return (
    <div className="area-tree-node-container">
      <div
        className="br-item text-interactive px-0 py-0"
        data-toggle
        style={{
          position: 'sticky',
          top: `${60 + depth * 57}px`,
          zIndex: 1,
          backgroundColor: 'rgba(255,255,255,0.96)',
        }}
        {...accessibleOnClick(() => setExpanded((v) => !v))}
      >
        <div className="content d-flex flex-row flex-wrap align-items-center gap-2 py-baseh px-1 justify-content-md-start justify-content-center">
          <div
            className={cx(
              'flex-fill text-up-02 area-tree-node__title d-flex align-items-center justify-content-md-start justify-content-center',
              { 'text-weight-semi-bold': expanded },
            )}
          >
            <span
              className={`text-up-02 mr-2 ${expanded ? 'fas fa-angle-up' : 'fas fa-angle-down'}`}
              aria-hidden="true"
              style={{ minWidth: '16px' }}
            />
            <span className={cx('w-100', areaReviewStateClass)}>
              {title || path}
            </span>
          </div>

          {token && expanded && (
            <div
              className={cx(
                'd-flex flex-column flex-sm-row gap-1 justify-content-start',
                styles['width-100'],
              )}
              role="toolbar"
              aria-label={intl.formatMessage(messages.areaToolsAria)}
            >
              {devActions.map(({ id: actionId, href, icon, label }) => (
                <ConditionalLink
                  key={actionId}
                  condition={!isEditMode}
                  href={href}
                  className={cx(
                    'br-button small text-weight-medium text-down-01 gap-2',
                    styles['width-100'],
                    { disabled: isEditMode },
                  )}
                  aria-label={label}
                  title={label}
                  style={isEditMode ? { pointerEvents: 'none' } : undefined}
                >
                  <MaybeWrap
                    condition={isEditMode}
                    as="span"
                    className={cx(
                      'br-button small text-weight-medium text-down-01 gap-2',
                      styles['width-100'],
                      { disabled: isEditMode },
                    )}
                  >
                    <span className={`fas ${icon}`} aria-hidden="true" />
                    <span>{label}</span>
                  </MaybeWrap>
                </ConditionalLink>
              ))}
            </div>
          )}
        </div>
        <span className="br-divider"></span>
      </div>

      <div className="area-tree-node-content">
        <AnimateHeight
          animateOpacity
          duration={600}
          height={expanded ? 'auto' : 0}
        >
          <div className="content pt-2 pb-5">
            {/* Informações da área acima das pessoas */}
            {hasLoaded && showAreasDescriptions && areaData && (
              <AreaInfo areaData={areaData} showTitle={showPersons} />
            )}

            {/* Lista de pessoas */}
            {hasLoaded && showPersons && persons.length > 0 && (
              <div className="ml-3">
                {showAreasDescriptions && showPersons && (
                  <div className="text-weight-medium text-up-01 mb-3">
                    {intl.formatMessage(messages.whoIsWho)}
                  </div>
                )}
                <div className={personsGridClass(personsPerRow, styles)}>
                  {persons.map((person) => (
                    <PersonCard
                      key={person['@id']}
                      person={person}
                      showDescription={personsShowDescription}
                      showPersonLink={peopleLinks}
                      summary={summaryCard}
                      showAllLinks={showPersonsAllLinks}
                      showSubstitute={showPersonsSubstitute}
                      isEditMode={isEditMode}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Subáreas */}
            {hasLoaded && (
              <div className="subareas ml-3">
                {subAreas.map((subArea) => (
                  <AreaTreeNode
                    id={`${id || path}:${subArea['@id']}`}
                    path={subArea['@id']}
                    title={subArea.title}
                    key={subArea['@id']}
                    personsPerRow={personsPerRow}
                    personsShowDescription={personsShowDescription}
                    showPersons={showPersons}
                    summaryCard={summaryCard}
                    depth={depth + 1}
                    area={subArea}
                    peopleLinks={peopleLinks}
                    showAreasDescriptions={showAreasDescriptions}
                    showPersonsAllLinks={showPersonsAllLinks}
                    showPersonsSubstitute={showPersonsSubstitute}
                    listingItems={listingItems}
                    hasLoaded={hasLoaded}
                  />
                ))}
              </div>
            )}

            {/* No info available */}
            {hasLoaded && persons.length === 0 && subAreas.length === 0 && (
              <div className="text-muted text-center">
                {intl.formatMessage({
                  id: 'listing.persons.empty',
                  defaultMessage:
                    'This area does not have people or subordinate areas registered yet',
                })}
              </div>
            )}
          </div>
        </AnimateHeight>
      </div>
    </div>
  );
}

AreaTreeNode.propTypes = {
  id: PropTypes.string,
  path: PropTypes.string.isRequired,
  title: PropTypes.string,
  listingItems: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  hasLoaded: PropTypes.bool,
  personsPerRow: PropTypes.oneOf(['1', '2', '3', '4', '5']),
  personsShowDescription: PropTypes.bool,
  showPersons: PropTypes.bool,
  summaryCard: PropTypes.bool,
  depth: PropTypes.number,
  area: PropTypes.object,
  peopleLinks: PropTypes.bool,
  showAreasDescriptions: PropTypes.bool,
  showPersonsAllLinks: PropTypes.bool,
  showPersonsSubstitute: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

export default AreaTreeNode;
